import {
  PdfSize,
  PdfRect,
  PdfBrush,
  PdfPen,
  PdfColor,
  PdfFont,
  ETextAlign,
  PageGen,
  PdfUnit,
} from '../../../pdf/pdf';
import { SizeConstraint, MeasureParams, DrawParams } from '../types';

export const ANNOTATION_COLORS: Record<string, PdfColor> = {
  Note: PdfColor.fromRgb(0, 0, 0),
  Warning: PdfColor.fromRgb(255, 165, 0),
  Error: PdfColor.fromRgb(255, 0, 0),
  Good: PdfColor.fromRgb(0, 128, 0),
};

export type HorizontalAlignment = 'Left' | 'Center' | 'Right' | 'Stretch';
export type VerticalAlignment = 'Top' | 'Center' | 'Bottom' | 'Stretch';

export function getHorizontalAlignment(control: {
  attrs: Record<string, string>;
}): HorizontalAlignment {
  return (control.attrs.HorizontalAlignment as HorizontalAlignment) || 'Stretch';
}

export function getVerticalAlignment(control: {
  attrs: Record<string, string>;
}): VerticalAlignment {
  return (control.attrs.VerticalAlignment as VerticalAlignment) || 'Stretch';
}

export const BLACK = PdfBrush.Black;
export const WHITE = PdfBrush.White;
export const DEFAULT_PEN = new PdfPen(PdfColor.fromRgb(0, 0, 0), 0.5);

export const MM = (value: number) => value;
export const GAP = 3;
export const MARGIN = 3;

export function drawTextClipped(
  page: PageGen,
  text: string,
  font: PdfFont,
  rect: PdfRect,
  align: ETextAlign = ETextAlign.CenterLeft
): void {
  if (!text) return;

  const textSize = page.measureString(text, font);
  const y = rect.y + Math.max(0, (rect.height - textSize.height) / 2);
  const visibleHeight = Math.max(0, Math.min(textSize.height, rect.height));
  if (visibleHeight <= 0) return;

  const isCenter = align === ETextAlign.Center;
  const isRight =
    align === ETextAlign.TopRight ||
    align === ETextAlign.CenterRight ||
    align === ETextAlign.BottomRight;

  let x: number;
  let visibleWidth: number;
  if (isCenter) {
    const offset = Math.max(0, (rect.width - textSize.width) / 2);
    x = rect.x + offset;
    visibleWidth = Math.max(0, Math.min(textSize.width, rect.width - offset));
  } else if (isRight) {
    const offset = Math.max(0, rect.width - textSize.width);
    x = rect.x + offset;
    visibleWidth = Math.max(0, Math.min(textSize.width, rect.width - offset));
  } else {
    x = rect.x;
    visibleWidth = Math.max(0, Math.min(textSize.width, rect.width));
  }

  if (visibleWidth > 0) {
    page.drawString(
      text,
      font,
      BLACK,
      null,
      new PdfRect(x, y, visibleWidth, visibleHeight),
      ETextAlign.CenterLeft
    );
  }
}

export function getExplicitSize(control: { attrs: Record<string, string> }): {
  width: number;
  height: number;
} {
  const w = parseFloat(control.attrs.Width);
  const h = parseFloat(control.attrs.Height);
  return {
    width: w > 0 ? PdfUnit.FromMillimeters(w) : 0,
    height: h > 0 ? PdfUnit.FromMillimeters(h) : 0,
  };
}

export function applyExplicitSize(
  desired: PdfSize,
  explicit: { width: number; height: number }
): PdfSize {
  return new PdfSize(explicit.width || desired.width, explicit.height || desired.height);
}

export function constrainSize(desired: PdfSize, constraint: SizeConstraint): PdfSize {
  return new PdfSize(
    Math.min(desired.width, constraint.width),
    Math.min(desired.height, constraint.height)
  );
}

export function measureText(text: string, font: PdfFont, page: PageGen | undefined): PdfSize {
  if (!text) return new PdfSize(0, 0);
  return page?.measureString(text, font) || new PdfSize(text.length * 3, 10);
}
